/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { speak } from "../lib/i18n";
import { toast } from "sonner";
import { X, Smile, Hand, MessageCircleOff, Siren, Phone, Volume2, Check, Loader2, WifiOff, MapPin, MessageCircle, MessageSquare, Mic, Square, Trash2 } from "lucide-react";

const MAX_REC_S = 60;
const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
const blobToB64 = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });

const TEAM_KEY = "bc_care_team";
export const loadCachedTeam = () => { try { return JSON.parse(localStorage.getItem(TEAM_KEY) || "null"); } catch { return null; } };
export const fetchTeam = (patientId) => api.get(`/patients/${patientId}/care-team`).then(({ data }) => { localStorage.setItem(TEAM_KEY, JSON.stringify(data)); return data; });

export const SIGNES = [
  { code: "visage", lettre: "V", Icon: Smile, fr: "Visage : un côté tombe quand il sourit ?", ln: "Elongi : ngámbo moko ekiti soki asɛki ?" },
  { code: "bras", lettre: "I", Icon: Hand, fr: "Incapacité : un bras ne se lève pas ou retombe ?", ln: "Lobɔkɔ : lobɔkɔ moko etombwami te to ekwei ?" },
  { code: "parole", lettre: "T", Icon: MessageCircleOff, fr: "Trouble de la parole : mots confus, ne comprend pas ?", ln: "Koloba : maloba mobulu, azali kososola te ?" },
  { code: "urgence", lettre: "E", Icon: Siren, fr: "Extrême urgence : un seul signe suffit, appelez tout de suite !", ln: "Urgence : elembo moko ekoki, benga sikoyo !" },
];

export default function WorryModePage() {
  const navigate = useNavigate();
  const { patient, lang, online } = useApp();
  const { user } = useAuth();
  const [checked, setChecked] = useState([]);
  const [team, setTeam] = useState(loadCachedTeam);
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(null); // {lat, lng, precision_m}
  const [posState, setPosState] = useState("idle"); // idle | loading | ok | denied
  const [rec, setRec] = useState({ state: "idle", secs: 0, blob: null, mime: "", duree: 0 });
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const startedAt = useRef(0);
  const ln = lang === "ln";
  const micOk = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  useEffect(() => () => { clearInterval(timer.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const locate = () => new Promise((resolve) => {
    if (!navigator.geolocation) { setPosState("denied"); resolve(null); return; }
    setPosState("loading");
    navigator.geolocation.getCurrentPosition(
      (p) => { const v = { lat: p.coords.latitude, lng: p.coords.longitude, precision_m: Math.round(p.coords.accuracy) }; setPos(v); setPosState("ok"); resolve(v); },
      () => { setPosState("denied"); toast.error(ln ? "Esika ezwami te. Pesa nzela na GPS." : "Position indisponible. Autorisez la localisation."); resolve(null); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 });
  });
  const mapsUrl = (p) => `https://maps.google.com/?q=${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
  const posMessage = (p) => `URGENT Bombacare — ${user?.nom || "L'aidant"} a besoin d'aide pour ${team?.patient_nom || "le patient"} (suspicion AVC${checked.length ? ` : ${checked.join(", ")}` : ""}). Position : ${mapsUrl(p)} (± ${p.precision_m || "?"} m)`;
  const shareWhatsApp = async () => {
    const p = pos || (await locate()); if (!p) return;
    const tel = (team?.therapeute?.telephone || "").replace(/[^\d]/g, "");
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(posMessage(p))}`, "_blank", "noopener");
  };
  const shareSms = async () => {
    const p = pos || (await locate()); if (!p) return;
    const nums = [numero, team?.therapeute?.telephone].filter(Boolean).join(",");
    window.location.href = `sms:${nums}?body=${encodeURIComponent(posMessage(p))}`;
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.onstop = () => {
        const duree = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000));
        const blob = new Blob(chunks.current, { type: r.mimeType || mime || "audio/webm" });
        setRec({ state: "done", secs: duree, blob: blob.size > 500 ? blob : null, mime: blob.type, duree });
      };
      r.start(250); recRef.current = r; startedAt.current = Date.now();
      setRec({ state: "rec", secs: 0, blob: null, mime: "", duree: 0 });
      timer.current = setInterval(() => { const s = Math.round((Date.now() - startedAt.current) / 1000); setRec((x) => ({ ...x, secs: s })); if (s >= MAX_REC_S) stopRec(); }, 500);
    } catch { toast.error("Micro indisponible. Autorisez l'accès au micro."); }
  };
  const stopRec = () => { clearInterval(timer.current); const r = recRef.current; if (r && r.state !== "inactive") { r.stop(); r.stream.getTracks().forEach((t) => t.stop()); } };

  useEffect(() => {
    if (!patient) return;
    fetchTeam(patient.id).then(setTeam).catch(() => null);
    const t = setTimeout(() => speak((ln ? "Kimia. Tala bilembo minei ya AVC. " : "Restez calme. Vérifiez les quatre signes d'AVC. ") + SIGNES.map((s) => (ln ? s.ln : s.fr)).join(" "), lang), 400);
    return () => { clearTimeout(t); window.speechSynthesis?.cancel(); };
  }, [patient]); // eslint-disable-line

  const toggle = (c) => setChecked((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c]));
  const numero = team?.numero_urgence || user?.numero_urgence || "112";

  const alertTeam = async () => {
    if (!patient || sent) return;
    setBusy(true);
    const body = { patient_id: patient.id, signes: checked, position: pos };
    if (rec.blob) { body.audio_b64 = await blobToB64(rec.blob); body.mime = rec.mime || "audio/webm"; body.duree_s = rec.duree; }
    const r = await postOrQueue("/alerts/worry", body);
    setSent(r);
    if (r._queued) toast.info("Hors-ligne : le praticien sera prévenu dès le retour du réseau. Appelez directement.");
    else toast.success("Votre praticien est prévenu");
    setBusy(false);
  };

  return (
    <div data-testid="worry-mode" className="fixed inset-0 z-50 bg-[#7a1f1c] text-white flex flex-col overflow-y-auto">
      {!online && (
        <div data-testid="worry-offline-banner" className="bg-[#D4AF5A] text-[#0A1426] text-[12px] font-bold px-4 py-2 flex items-center gap-2"><WifiOff size={14} /> {ln ? "Hors-ligne — banimero ezali na cache" : "Hors-ligne — numéros en cache, appel possible"}</div>
      )}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2"><Siren size={22} className="text-[#D4AF5A]" /><span className="font-[Fraunces] text-xl font-bold">{ln ? "Nazali na mitungisi" : "Je suis inquiet"}</span></div>
        <button data-testid="worry-close" onClick={() => navigate(-1)} className="w-11 h-11 rounded-full bg-white/15 grid place-items-center"><X size={22} /></button>
      </div>
      <div className="px-4 pb-6 flex flex-col gap-3 max-w-md w-full mx-auto">
        <div className="flex items-center justify-between">
          <p className="text-[14px] text-white/90 leading-snug">{ln ? "Tala bilembo minei (VITE). Fina oyo omoni." : "Vérifiez les 4 signes (méthode VITE). Touchez ceux que vous observez."}</p>
          <button data-testid="worry-speak" onClick={() => speak(SIGNES.map((s) => (ln ? s.ln : s.fr)).join(" "), lang)} className="w-11 h-11 shrink-0 rounded-full bg-white/15 grid place-items-center"><Volume2 size={20} /></button>
        </div>
        {SIGNES.map((s) => {
          const on = checked.includes(s.code);
          return (
            <button key={s.code} data-testid={`sign-${s.code}`} onClick={() => toggle(s.code)}
              className={`w-full text-left rounded-3xl p-4 flex items-center gap-3 border-2 transition-colors ${on ? "bg-white text-[#7a1f1c] border-white" : "bg-white/10 border-white/20"}`}>
              <span className={`w-14 h-14 rounded-2xl grid place-items-center shrink-0 relative ${on ? "bg-[#7a1f1c]/10" : "bg-white/15"}`}>
                <s.Icon size={28} />
                <span className="absolute -top-1.5 -left-1.5 w-6 h-6 rounded-full bg-[#D4AF5A] text-[#0A1426] text-[12px] font-bold grid place-items-center">{s.lettre}</span>
              </span>
              <span className="flex-1 text-[15px] font-bold leading-snug">{ln ? s.ln : s.fr}</span>
              {on && <Check size={22} />}
            </button>
          );
        })}

        <a data-testid="worry-call-emergency" href={`tel:${numero}`} className="mt-2 h-20 rounded-3xl bg-white text-[#7a1f1c] font-[Fraunces] text-2xl font-bold flex items-center justify-center gap-3 active:scale-[0.98]">
          <Phone size={28} /> {ln ? "Benga urgences" : "Appeler les urgences"} {numero}
        </a>
        {team?.therapeute?.telephone && (
          <a data-testid="worry-call-therapist" href={`tel:${team.therapeute.telephone}`} className="h-16 rounded-3xl bg-[#0A1426] text-white font-bold text-lg flex items-center justify-center gap-3">
            <Phone size={22} /> {ln ? "Benga" : "Appeler"} {team.therapeute.nom}
          </a>
        )}

        <section data-testid="worry-position" className="rounded-3xl bg-white/10 border border-white/20 p-3 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[14px] font-bold"><MapPin size={18} className="text-[#D4AF5A]" /> {ln ? "Kabola esika na ngai" : "Partager ma position"}</div>
            <button data-testid="worry-locate" onClick={locate} disabled={posState === "loading"} className="h-9 px-3 rounded-full bg-white/15 text-[12px] font-bold flex items-center gap-1">
              {posState === "loading" ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />} {posState === "ok" ? (ln ? "Ezwami" : "Position trouvée") : (ln ? "Zwa esika" : "Localiser")}
            </button>
          </div>
          {pos && <a data-testid="worry-position-link" href={mapsUrl(pos)} target="_blank" rel="noreferrer" className="text-[11px] text-white/80 underline break-all">{mapsUrl(pos)} (± {pos.precision_m} m)</a>}
          {posState === "denied" && <div className="text-[11px] text-[#D4AF5A]">{ln ? "GPS ekangami. Fungola localisation na téléphone." : "Localisation refusée — activez le GPS et réessayez."}</div>}
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="worry-share-whatsapp" onClick={shareWhatsApp} className="h-12 rounded-2xl bg-[#25D366] text-white font-bold text-[13px] flex items-center justify-center gap-2"><MessageCircle size={16} /> WhatsApp</button>
            <button data-testid="worry-share-sms" onClick={shareSms} className="h-12 rounded-2xl bg-white text-[#7a1f1c] font-bold text-[13px] flex items-center justify-center gap-2"><MessageSquare size={16} /> SMS {ln ? "urgences" : "secours"}</button>
          </div>
        </section>

        {micOk && (
          <section data-testid="worry-voice" className="rounded-3xl bg-white/10 border border-white/20 p-3 flex items-center gap-3">
            <button data-testid="worry-rec-btn" onClick={rec.state === "rec" ? stopRec : startRec} disabled={rec.state === "done" && !!rec.blob}
              className={`w-14 h-14 rounded-full grid place-items-center shrink-0 ${rec.state === "rec" ? "bg-[#D4AF5A] text-[#7a1f1c] animate-pulse" : "bg-white text-[#7a1f1c]"} disabled:opacity-50`}>
              {rec.state === "rec" ? <Square size={22} /> : <Mic size={24} />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold">{ln ? "Limbola makambo na mongongo" : "Décrire la situation (vocal)"}</div>
              <div data-testid="worry-rec-state" className="text-[11px] text-white/80">
                {rec.state === "rec" ? `${rec.secs}s · ${ln ? "fina mpo na kosilisa" : "appuyez pour arrêter"} (max ${MAX_REC_S}s)` : rec.blob ? `${ln ? "Enregistré" : "Enregistré"} · ${rec.duree}s · ${ln ? "ekotindama na alerte" : "joint au signalement"}` : (ln ? "Ekotindama na praticien, ekokomama" : "Envoyé au praticien avec le signalement, transcrit automatiquement")}
              </div>
            </div>
            {rec.blob && <button data-testid="worry-rec-delete" aria-label="Supprimer" onClick={() => setRec({ state: "idle", secs: 0, blob: null, mime: "", duree: 0 })} className="w-10 h-10 rounded-full bg-white/15 grid place-items-center"><Trash2 size={16} /></button>}
          </section>
        )}
        <button data-testid="worry-notify" disabled={busy || !!sent} onClick={alertTeam}
          className={`h-14 rounded-3xl font-bold text-[15px] flex items-center justify-center gap-2 ${sent ? "bg-[#5CB85C] text-white" : "bg-[#D4AF5A] text-[#0A1426]"} disabled:opacity-90`}>
          {busy ? <Loader2 className="animate-spin" size={18} /> : sent ? <><Check size={18} /> {sent._queued ? (ln ? "Ekotindama soki réseau ezongi" : "Sera envoyé au retour du réseau") : (ln ? "Praticien ayebi" : "Praticien prévenu")}</> : (ln ? "Yebisa praticien (push)" : "Prévenir le praticien (notification)")}
        </button>
        {(user?.contacts_urgence || []).length > 0 && (
          <div data-testid="worry-family-contacts" className="rounded-2xl bg-white/10 px-3 py-2 text-[12px] text-white/90 flex items-start gap-2">
            <MessageSquare size={14} className="mt-0.5 shrink-0 text-[#D4AF5A]" />
            <span>
              {sent && !sent._queued
                ? (ln ? "Bandeko bayebisami na SMS : " : "Proches prévenus par SMS : ")
                : sent ? (ln ? "SMS ekotindama na bandeko soki réseau ezongi : " : "SMS aux proches au retour du réseau : ")
                : (ln ? "SMS ekotindama automatiquement na : " : "Un SMS avec les signes et la position partira automatiquement à : ")}
              <b>{(user.contacts_urgence || []).map((c) => c.nom || c.telephone).join(", ")}</b>
            </span>
          </div>
        )}
        <p className="text-[11px] text-white/70 text-center leading-snug">{ln ? "Kokanga ntango te : AVC ezali urgence. Kopesa ye eloko ya kolia to komela te." : "Ne perdez pas de temps : un AVC est une urgence. Ne donnez ni à boire ni à manger. Notez l'heure des premiers signes."}</p>
      </div>
    </div>
  );
}
